import React, { forwardRef, useMemo, ReactNode, useRef } from 'react';

import { Listbox } from "@headlessui/react";

import { DGACheckboxInput } from '../dga-checkbox-input/DGACheckboxInput';

import Arrow from '../icons/arrow';

import { 
    Variant, 
    SizeVariant, 
    WeightVariant,
    LabelSizeMap,
    sizeMap,
    baseClasses,
    getWrapperClasses
} from 'lib/dga-input-components-variants/dga-inputs-variants';

import IsRtl from '../isRtl';

const ChechIcon = () => (
    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M16.265 5.4649C16.5146 5.70323 16.5238 6.09885 16.2855 6.34854L7.53551 15.5152C7.41932 15.6369 7.25895 15.7067 7.09068 15.7086C6.92241 15.7106 6.76046 15.6446 6.64147 15.5256L3.72481 12.6089C3.48073 12.3649 3.48073 11.9691 3.72481 11.7251C3.96888 11.481 4.36461 11.481 4.60869 11.7251L7.07302 14.1894L15.3813 5.48545C15.6197 5.23576 16.0153 5.22656 16.265 5.4649Z" fill="#161616" />
    </svg>
)

interface DropdownOption {
    value: string | number;
    label: string;
    disabled?: boolean;
    [key: string]: any;
}

interface DGADropdownInputProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value" | "onChange"> {
    options: DropdownOption[];
    value?: string | number | (string | number)[] | {[key: string]: any}[];
    onChange?: (value: string | number | (string | number)[] | {[key: string]: any}[]) => void;
    onBlur?: (e: any) => void;
    multiple?: boolean;
    emptyText?: string;
    placeholder?: string;
    maxHeight?: number;
    closeAfterSelect?: boolean;

    renderOption?: (option: DropdownOption, isSelected: boolean) => ReactNode;
    singleSelectIcon?: ReactNode;
    startIcon?: ReactNode;
    endIcon?: ReactNode;

    variant?: Variant;
    InputSize?: SizeVariant;
    LabelSize?: SizeVariant;
    LabelWeight?: WeightVariant;
    label?: string;
    name?: string;
    error?: boolean;
    errorMessage?: string | React.ReactNode;
    helperText?: ReactNode;
    disabled?: boolean;
    required?: boolean;
    requiredIndicator?: ReactNode;

    dropdownClassName?: string;
    optionClassName?: string;
    customDisplayValue?: string;
    mainDropdownContainerClassName?: string;

    containerClassName?: string;
    inputWrapperClassName?: string;
    inputClassName?: string;
    labelClassName?: string;
    errorClassName?: string;
    helperTextClassName?: string;
}

const DGADropdownInput = forwardRef<HTMLButtonElement, DGADropdownInputProps>(({
    options = [],
    value,
    onChange,
    multiple = false,
    emptyText = "No options available",
    closeAfterSelect = false,
    maxHeight,

    renderOption,
    singleSelectIcon,
    startIcon,
    endIcon,

    variant = 'default',
    InputSize = 'lg',
    LabelSize = 'md',
    LabelWeight = 'regular',
    label,
    name,
    error = false,
    errorMessage,
    helperText,
    disabled = false,
    required = false,
    requiredIndicator,

    dropdownClassName = '',
    optionClassName = '',
    customDisplayValue = undefined,
    mainDropdownContainerClassName = '',

    containerClassName = '',
    inputWrapperClassName = '',
    inputClassName = '',
    labelClassName = '',
    errorClassName = '',
    helperTextClassName = '',
    placeholder,
}, ref) => {
    const size = sizeMap[InputSize];
    const labelSize = `${LabelSizeMap[LabelSize]} ${LabelSizeMap[LabelWeight]}`;

    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const normalizedValue = useMemo(() => {
        if (multiple) {
            return Array.isArray(value) ? value : (value !== undefined ? [value] : []);
        }
        return Array.isArray(value) ? value[0] : value;
    }, [value, multiple]);

    const displayValue = useMemo(() => {
        if (customDisplayValue) return customDisplayValue;

        if (multiple) {
            const selectedOptions = options?.filter(option =>
                Array.isArray(normalizedValue) && normalizedValue?.includes(option?.value)
            );
            return selectedOptions?.length > 0
                ? selectedOptions?.map(option => option?.label).join(', ')
                : placeholder;
        } else {
            const selectedOption = options?.find(option => option.value === normalizedValue);
            return selectedOption ? selectedOption.label : placeholder;
        }
    }, [normalizedValue, options, multiple, customDisplayValue, placeholder]);

    const isShowingPlaceholder = useMemo(() => {
        if (customDisplayValue) return false;
        if (!placeholder) return false;

        if (multiple) {
            return !Array.isArray(normalizedValue) || normalizedValue.length === 0;
        }

        return normalizedValue === undefined || normalizedValue === null || normalizedValue === "";
    }, [customDisplayValue, multiple, normalizedValue, placeholder]);

    const selectedOption = useMemo(() => {
        if (multiple) {
            return options?.filter(option =>
                Array.isArray(normalizedValue) && normalizedValue?.includes(option?.value)
            );
        } else {
            return options?.find(option => option?.value === normalizedValue);
        }
    }, [normalizedValue, options, multiple]);

    const handleOptionChange = (option: DropdownOption | DropdownOption[]) => {
        if (multiple && Array.isArray(option)) {
            const newValues = option?.map(opt => opt?.value);
            onChange?.(newValues);
            if (!closeAfterSelect) buttonRef?.current?.click();
        } else if (!multiple && !Array.isArray(option)) {
            const isCurrentlySelected = normalizedValue === option?.value;
            const newValue = isCurrentlySelected ? undefined : option?.value;
            onChange?.(newValue as any);
            if (!closeAfterSelect) buttonRef?.current?.click();
        }
    };

    const isOptionSelected = (option: DropdownOption) => {
        if (multiple) {
            return Array.isArray(normalizedValue) && normalizedValue.includes(option?.value);
        }
        return normalizedValue === option?.value;
    };

    const renderOptionContent = (option: DropdownOption, isSelected: boolean) => {
        if (renderOption) return renderOption(option, isSelected);

        return (
            <div className="flex items-center gap-2 w-full p-2">
                {multiple && (
                    <DGACheckboxInput
                      size="xs"
                      variant="default"
                      checked={isSelected}
                      aria-label={String(option?.label ?? "")}
                    />
                )}
                <span
                    title={option?.label}
                    className="flex-1 text-start rtl:text-right line-clamp-1"
                >
                    {option?.label}
                </span>
                {!multiple && isSelected && (
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                        {singleSelectIcon || <ChechIcon />}
                    </span>
                )}
            </div>
        );
    };

    const wrapperClasses = getWrapperClasses(
        'dropdown', 
        variant, 
        error, 
        disabled, 
        startIcon, 
        true, 
        inputWrapperClassName
    );

    const finalInputClasses = `${baseClasses.inputBase(size, false, disabled)} ${inputClassName}`.trim();
    const finalLabelClasses = `${baseClasses.labelBase(disabled, labelSize)} ${labelClassName}`.trim();
    const finalErrorClasses = `${baseClasses.errorBase} ${errorClassName}`.trim();
    const finalDescriptionClasses = `${baseClasses.descriptionBase} ${helperTextClassName}`.trim();
    const finalContainerClasses = `${baseClasses.containerBase} ${containerClassName}`.trim();

    const handleLabelClick = () => {
        if (buttonRef?.current) {
            const isOpen = buttonRef?.current.getAttribute('aria-expanded') === 'true';
            if (!isOpen) {
                buttonRef?.current.click();
            } else {
                buttonRef?.current.blur();
            }
        }
    }

    return (
        <div 
            dir={IsRtl() ? 'rtl' : 'ltr'}
            className={`${finalContainerClasses} ${mainDropdownContainerClassName}`} 
        >
            {label && (
                <label 
                    className={finalLabelClasses} 
                    onClick={handleLabelClick}
                >
                    {label}
                    {required && (requiredIndicator || <span className="text-red-500">*</span>)}
                </label>
            )}
            
            <Listbox 
                value={selectedOption} 
                onChange={handleOptionChange}
                multiple={multiple}
                disabled={disabled}
                name={name}
            >
                {({ open }) => (
                    <div className="relative w-full min-w-[200px]">
                        <Listbox.Button
                            ref={(node: any) => {
                                buttonRef.current = node;
                                if (typeof ref === "function") ref(node);
                                else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
                            }}
                            className={`${wrapperClasses} cursor-pointer focus-within:border-transparent focus-within:before:scale-x-0 before:transition-transform before:scale-x-0 ${open ? `!bg-white ${error ? '' : '!border-neutral-400'} before:scale-x-100 ` : (variant === 'Filled-Darker' ? `!bg-neutral-100 ${error ? '' : 'hover:!border-neutral-400 !border-transparent'}` : '')}`}
                        >
                            {startIcon && (
                                <span className="flex-shrink-0 flex items-center justify-center">
                                    {startIcon}
                                </span>
                            )}
                            
                            <span className={`${finalInputClasses} ${isShowingPlaceholder ? '!text-neutral-500' : ''}`.trim()}>
                                {displayValue || placeholder}
                            </span>
                            
                            {(endIcon || !startIcon) && (
                                <span className="flex-shrink-0 flex items-center justify-center">
                                    {endIcon || (
                                        <span
                                            className={`transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
                                        >
                                            <Arrow fill={disabled ? '#9DA4AE' : '#000000'} aria-label="" aria-hidden={true} />
                                        </span>
                                    )}
                                </span>
                            )}
                        </Listbox.Button>

                        <Listbox.Options
                            dir={IsRtl() ? 'rtl' : 'ltr'}
                            className={`
                                absolute top-full left-0 rtl:left-auto rtl:right-0 z-50 w-full min-w-[200px] bg-white border border-neutral-300 rounded-md shadow-lg p-2 mt-1

                                focus-within:outline-none
                                overflow-y-auto
                                ${dropdownClassName}
                            `}
                            style={{
                                boxShadow: '0px 20px 24px -4px #10182814',
                                maxHeight: maxHeight ? `${maxHeight}px` : '300px',
                            }}
                        >
                            <div className="overflow-y-auto space-y-0.5">
                                {options?.length === 0 ? (
                                    <div className="text-neutral-500 text-center p-2">
                                        {emptyText}
                                    </div>
                                ) : (
                                    options?.map((option) => {
                                        const isSelected = isOptionSelected(option);

                                        return (
                                            <Listbox.Option
                                                key={option?.value}
                                                value={option}
                                                disabled={option?.disabled}
                                                className={({ active }) => `
                                                    cursor-pointer transition-colors rounded w-full
                                                    bg-white hover:bg-neutral-100 active:bg-neutral-200 
                                                    focus:bg-white
                                                    border-2 border-transparent focus:!border-primary-1000 focus:!outline-none
                                                    ${isSelected && 'bg-neutral-200'}
                                                    ${option?.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                                                    ${active ? 'bg-neutral-100' : ''}
                                                    ${optionClassName}
                                                `}
                                            >
                                                {renderOptionContent(option, isSelected)}
                                            </Listbox.Option>
                                        );
                                    })
                                )}
                            </div>
                        </Listbox.Options>
                    </div>
                )}
            </Listbox>

            {helperText && !error && (
                <span className={finalDescriptionClasses}>
                    {helperText}
                </span>
            )}
            
            {error && (
                <span className={finalErrorClasses}>
                    {errorMessage}
                </span>
            )}
        </div>
    );
});

DGADropdownInput.displayName = 'DGADropdownInput';
export default DGADropdownInput;
